import axios from "axios";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { useState } from "react";
import { downloadFile } from "../utils/file.ts";
import type { ResourceType } from "../views/Board/types.ts";

export const useDownload = () => {
  const [downloading, setDownloading] = useState(false);

  return {
    mutate: ({
      items,
      onSuccess,
      onError,
      onBeforeDownload,
    }: {
      items: { url: string; filename: string; type: ResourceType }[];
      onSuccess?: () => void;
      onError?: () => void;
      onBeforeDownload?: (
        url: string,
        type: ResourceType,
      ) => Promise<Blob> | Blob;
    }) => {
      setDownloading(true);

      const processItems = async () => {
        if (onBeforeDownload) {
          const processedItems = await Promise.all(
            items.map(async (item) => ({
              filename: item.filename,
              blob: await onBeforeDownload(item.url, item.type),
            })),
          );
          return processedItems;
        }

        const processedItems = await Promise.all(
          items.map(async (item) => {
            const response = await axios.get<Blob>(item.url, {
              responseType: "blob",
            });
            return {
              filename: item.filename,
              blob: response.data,
            };
          }),
        );
        return processedItems;
      };

      processItems()
        .then((processedItems) => {
          if (processedItems.length === 1) {
            downloadFile(processedItems[0].blob, processedItems[0].filename);
            onSuccess?.();
            setDownloading(false);
          } else {
            const zip = new JSZip();
            for (const { blob, filename } of processedItems) {
              zip.file(filename, blob);
            }

            zip
              .generateAsync({ type: "blob" })
              .then((blob) => {
                saveAs(blob, "pimento.zip");
                onSuccess?.();
              })
              .catch(() => {
                onError?.();
              })
              .finally(() => setDownloading(false));
          }
        })
        .catch(() => {
          onError?.();
          setDownloading(false);
        });
    },
    isLoading: downloading,
  };
};
